import React, { useState, useEffect } from 'react';
import Timeline from './components/Timeline';
import DocumentViewer from './components/DocumentViewer';
import ChatAnalyst from './components/ChatAnalyst';
import { Event, Document } from './types';
import { Calendar, FileText, MessageSquare } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function App() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch documents when an event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchDocuments(selectedEvent.id);
    } else {
      setDocuments([]);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      setEvents(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (eventId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/documents`);
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      const data = await response.json();
      setDocuments(data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setDocuments([]);
    }
  };

  const handleEventSelect = (event: Event) => {
    setSelectedEvent(event);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loader mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchEvents}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Legal Case Review</h1>
                <p className="text-sm text-gray-600">Interactive Case Analysis Platform</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {events.length} events loaded
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
          {/* Main Panel - Timeline */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
            <div className="flex items-center px-6 py-4 border-b border-gray-200">
              <Calendar className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Case Chronology</h2>
            </div>
            <div className="flex-1 overflow-hidden">
              <Timeline
                events={events}
                selectedEvent={selectedEvent}
                onEventSelect={handleEventSelect}
              />
            </div>
          </div>

          {/* Right Side Panels */}
          <div className="space-y-6">
            {/* Document Viewer */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-1/2">
              <div className="flex items-center px-6 py-4 border-b border-gray-200">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
                {selectedEvent && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({documents.length} files)
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <DocumentViewer
                  selectedEvent={selectedEvent}
                  documents={documents}
                />
              </div>
            </div>

            {/* AI Chat Analyst */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-1/2">
              <div className="flex items-center px-6 py-4 border-b border-gray-200">
                <MessageSquare className="h-5 w-5 text-purple-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">AI Analyst</h2>
              </div>
              <div className="flex-1 overflow-hidden">
                <ChatAnalyst
                  events={events}
                  selectedEvent={selectedEvent}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
